import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Upload, Loader2, Camera, Sparkles, ArrowRight, ImageIcon, X, CheckCircle } from 'lucide-react';
import { DOOR_MATERIALS, VENEER_SPECIES, MICA_PALETTE, PU_PAINT_PALETTE, DOOR_ROOMS } from '../lib/designStudio';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MatchMyDoors = () => {
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) { toast.error('Please upload an image under 8MB.'); return; }
    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
    setAnalysis(null);
    setError(null);
  };

  const analyze = async () => {
    if (!file) return;
    setIsAnalyzing(true);
    setError(null);
    setAnalysis(null);
    try {
      const dataUrl = await new Promise((res, rej) => {
        const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file);
      });
      const r = await fetch(`${API_URL}/api/match-doors`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ image: dataUrl }),
      });
      if (!r.ok) throw new Error('Analysis failed');
      const d = await r.json();
      if (!d.success) throw new Error(d.detail || 'Analysis failed');
      setAnalysis(d.analysis);
    } catch (e) {
      setError(e.message || 'Could not analyze the photo. Please try another image.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const reset = () => {
    setFile(null); setPreviewUrl(null); setAnalysis(null); setError(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  const suggestedMaterial = analysis ? DOOR_MATERIALS.find((m) => m.id === analysis.material_family_guess) : null;

  return (
    <div className="min-h-screen bg-[#F7F8F5]" data-testid="match-my-doors-page">
      {/* Hero */}
      <section className="relative bg-[#0A0D0C] pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 noise-overlay pointer-events-none" />
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#004534]/30 to-transparent" />
        <div className="container mx-auto px-6 lg:px-12 relative z-10 max-w-4xl">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-[#0A0D0C] text-sm font-bold mb-6">
              <Sparkles className="h-4 w-4" /> AI COHESION FINDER
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 font-['Outfit'] leading-tight">
              Match My Existing Doors
            </h1>
            <p className="text-lg text-[#D4AF37] font-medium mb-3">
              Upload a photo of any door in your home — our AI finds matching CriLam doors for the rest of your rooms.
            </p>
            <p className="text-white/70 max-w-2xl">
              Home cohesion matters. If your bedroom door is Walnut Veneer, your other doors should complement it. Our Design Guru AI
              identifies the material, color and style — then recommends CriLam options that create a coherent premium look.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-14">
        <div className="container mx-auto px-6 lg:px-12 max-w-4xl">

          {/* Upload area */}
          {!previewUrl && (
            <div
              onClick={() => inputRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const f = e.dataTransfer.files?.[0];
                if (f && f.type.startsWith('image/')) {
                  setFile(f); setPreviewUrl(URL.createObjectURL(f)); setAnalysis(null); setError(null);
                }
              }}
              className="border-2 border-dashed border-gray-300 rounded-2xl p-14 text-center bg-white hover:border-[#004534] transition-colors cursor-pointer"
              data-testid="match-upload-area"
            >
              <input ref={inputRef} type="file" accept="image/*" onChange={onPick} className="hidden" data-testid="match-file-input" />
              <div className="w-20 h-20 mx-auto mb-4 bg-[#004534]/10 rounded-full flex items-center justify-center">
                <Camera className="h-10 w-10 text-[#004534]" />
              </div>
              <h3 className="text-xl font-bold text-[#004534] mb-2">Upload a photo of an existing door</h3>
              <p className="text-gray-500 mb-4">JPG / PNG · Max 8MB · Bedroom, main entrance, any door in your home works</p>
              <button className="px-6 py-3 bg-[#D4AF37] text-[#004534] font-bold rounded-lg hover:bg-[#004534] hover:text-white transition-colors">
                <Upload className="h-4 w-4 inline mr-1.5" /> Choose Photo
              </button>
            </div>
          )}

          {/* Preview + analyze */}
          {previewUrl && !analysis && (
            <div className="grid md:grid-cols-2 gap-6">
              <div className="relative aspect-square bg-gray-100 rounded-xl overflow-hidden">
                <img src={previewUrl} alt="Your existing door" className="w-full h-full object-cover" />
                <button onClick={reset} className="absolute top-3 right-3 p-2 bg-white/90 rounded-full hover:bg-white" data-testid="match-clear-btn">
                  <X className="h-4 w-4 text-gray-600" />
                </button>
              </div>
              <div className="flex flex-col justify-center">
                <h3 className="text-2xl font-bold text-[#004534] mb-3 font-['Outfit']">Ready to analyze</h3>
                <p className="text-gray-600 mb-6">
                  Our Design Guru AI will identify the material family (laminate / mica / veneer / paint), dominant colour, style,
                  and recommend 2–3 CriLam doors that create home-wide cohesion.
                </p>
                {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">{error}</div>}
                <button
                  onClick={analyze}
                  disabled={isAnalyzing}
                  className="px-6 py-4 bg-[#004534] text-white rounded-lg font-bold hover:bg-[#D4AF37] hover:text-[#004534] transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="match-analyze-btn"
                >
                  {isAnalyzing ? <><Loader2 className="h-5 w-5 animate-spin" /> Analyzing…</> : <><Sparkles className="h-5 w-5" /> Analyze & Get Recommendations</>}
                </button>
              </div>
            </div>
          )}

          {/* Analysis result */}
          {analysis && (
            <div className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="relative aspect-square bg-gray-100 rounded-xl overflow-hidden">
                  <img src={previewUrl} alt="Your existing door" className="w-full h-full object-cover" />
                  <div className="absolute top-3 left-3 px-2 py-1 bg-black/60 text-white text-xs font-medium rounded-full">
                    Your Current Door
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <div className="text-xs text-gray-500 mb-1">AI Analysis</div>
                    <h3 className="text-2xl font-bold text-[#004534] font-['Outfit']">Cohesion Recommendations</h3>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <p className="text-gray-500 text-xs">Estimated Material</p>
                        <p className="font-bold text-[#004534] capitalize">{(analysis.material_family_guess || '').replace('-', ' ')}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs">Colour Family</p>
                        <p className="font-bold text-[#004534] capitalize">{(analysis.color_family || '').replace('-', ' ')}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs">Design Style</p>
                        <p className="font-bold text-[#004534] capitalize">{analysis.style || '—'}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs">Suggested Match</p>
                        <p className="font-bold text-[#7A6621]">{suggestedMaterial?.name || '—'}</p>
                      </div>
                    </div>
                    {analysis.insight && (
                      <div className="mt-3 pt-3 border-t text-xs text-gray-600 italic">
                        <Sparkles className="h-3 w-3 inline text-[#D4AF37] mr-1" /> {analysis.insight}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Recommendations */}
              <div>
                <h4 className="text-lg font-bold text-[#004534] mb-3 font-['Outfit']">Recommended CriLam Doors for the Rest of Your Home</h4>
                <div className="grid md:grid-cols-3 gap-4">
                  {(analysis.recommendations || []).slice(0, 3).map((rec, i) => {
                    const mat = DOOR_MATERIALS.find((m) => m.id === rec.material_family);
                    const room = DOOR_ROOMS.find((r) => r.id === rec.room);
                    return (
                      <div key={i} className="bg-white rounded-xl p-4 border border-gray-200 hover:border-[#004534] hover:shadow-md transition-all">
                        <div className={`h-24 rounded-lg mb-3 bg-gradient-to-br ${mat?.accentGradient || 'from-gray-100 to-gray-200'} flex items-center justify-center`}>
                          <CheckCircle className="h-8 w-8 text-[#004534]" />
                        </div>
                        <p className="text-xs text-gray-500 capitalize">{room?.label || 'Any room'}</p>
                        <p className="font-bold text-[#004534] mt-1">{mat?.name || rec.material_family}</p>
                        <p className="text-xs text-gray-600 mt-1 mb-3">{rec.why || 'Suggested by CriLam Design Guru'}</p>
                        <Link to="/door-designer">
                          <button className="w-full py-2 bg-[#004534] text-white text-sm rounded-lg hover:bg-[#D4AF37] hover:text-[#004534] transition-colors font-semibold flex items-center justify-center gap-1">
                            Design This <ArrowRight className="h-3 w-3" />
                          </button>
                        </Link>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="flex flex-wrap gap-3 justify-center pt-4">
                <Link to="/door-designer">
                  <button className="px-6 py-3 bg-[#D4AF37] text-[#004534] font-bold rounded-lg hover:bg-[#004534] hover:text-white transition-colors">
                    Start Designing → Door Designer
                  </button>
                </Link>
                <button onClick={reset} className="px-6 py-3 border-2 border-gray-300 text-gray-600 rounded-lg hover:border-[#004534] hover:text-[#004534]" data-testid="match-restart-btn">
                  <ImageIcon className="h-4 w-4 inline mr-1" /> Try Another Photo
                </button>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default MatchMyDoors;
